import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Checkbox
} from '@material-ui/core'
import i18n from 'i18next'
import { Trans } from 'react-i18next'
import { path } from 'ramda'
import { FC, Fragment, useEffect, useState, KeyboardEvent, ChangeEvent } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { UpdateStatus } from '../../../../actions/types'
import { CustomList } from '../../../../components/Display/Lists'
import { Message } from '../../../../components/Messages'
import {
  isApartmentCancelledDisabled,
  isApartmentMarketingDisabled
} from '../../../../libs/statuses'
import { MuiClasses } from '../../../../types'
import {
  Apartment,
  ApartmentStatus,
  CheckboxPropertyKeys
} from '../../../../types/apartments'
import { IndexedApplications } from '../../../../types/states'
import { User } from '../../../../types/users'
import { getSortedKeyArray, returnTrueFirst } from '../../../../utils/sorting'
import { getStepPath } from '../../../../utils/steps'
import {
  getAdditionalPropertiesForOverview,
  getApartmentBuildingInfo
} from '../../Forms/values/apartmentInitialValues'
import {
  ApartmentConfirmationList,
  ApartmentInformationList
} from '../Lists'
import { navigate } from '../../../../actions/routes'
import { useDispatch } from 'react-redux';
import '../../../PayForPublish/Rentapp.css';
import apartmentAPI from '../../../../services/apartments'
import { toast } from 'react-toastify'
import Dialog from '../../../Dialog'
import PaymentPopup from '../../../PayForPublish/PaymentPopup'
import Suggest from '../../Stepper/Steps/Suggest'
import DigivakuusFaqLink from '../../../../components/Digivakuus/DigivakuusFaqLink'
import ConfirmationDialogRaw from '../../../../components/ConfirmationDialogRaw/ConfirmationDialog'
import { BucketObject } from '../../../../services/storage/types'
import { MediaDialogButton } from './AddressAndMedia'

export interface ApartmentInfoProps {
  t: i18n.TFunction
  classes: MuiClasses
  apartment: Apartment
  user: User | undefined
  isAdmin: boolean
  updateStatus: UpdateStatus
  applications: IndexedApplications
  files: BucketObject[]
}

export const ApartmentAdminPanel: FC<ApartmentInfoProps> = ({
  t,
  classes,
  apartment,
  apartment: {
    applicationIds,
    status,
    information,
    confirmation,
    apartmentId
  },
  user,
  isAdmin,
  updateStatus,
  applications,
  files
}) => {
  const [selectedApplicationIdForRented, setselectedApplicationIdForRented] = useState('')
  const [isLoading, setIsLoading] = useState({ loading: false, func: '' })
  const selectedApplication =
    applicationIds &&
    applicationIds?.contractWantedId &&
    applications[apartmentId]
      ? applications[apartmentId].find(
          (application) =>
            application.applicationId === applicationIds.contractWantedId
        )
      : undefined
  const selectedContractWantedName = selectedApplication?.data?.name
    ? selectedApplication?.data?.name
    : undefined
  const apartmentRights = Array.isArray(apartment.rights) ? apartment.rights.map(String) : []
  const hasFollowUpRight = apartmentRights.includes('11')
  const showFollowUpActions =
    status === 'rentContractWanted' ||
    status === 'marketing' ||
    status === ApartmentStatus.followUp

  useEffect(() => {
    setIsLoading({
      loading: false,
      func: ''
    })
  }, [status])

  const dispatch = useDispatch()

  const redirectToPaymentPage = () => {
    if (!user) {
      return
    }
    dispatch(navigate(`/users/${user.userId}/apartments/${apartment.apartmentId}/payment`))
  }

  const handleApplicationChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setselectedApplicationIdForRented(event.target.value)
  }

  const [updateStatusToRentedClicked, setUpdateStatusToRentedClicked] = useState(false)
  const [updateStatusToRentedDialogHidden, setUpdateStatusToRentedDialogHidden] = useState(false)
  const [digivakuusDepositAccepted, setDigivakuusDepositAccepted] = useState(false)
  const [rentInsuranceInquiryAccepted, setRentInsuranceInquiryAccepted] = useState(false)
  const [ropoRentChargingServiceAccepted, setRopoRentChargingServiceAccepted] = useState(false)
  const [confirmFollowUpOpen, setConfirmFollowUpOpen] = useState(false)

  const hideUpdateStatusToRentedDialog = () => {
    setDigivakuusDepositAccepted(false)
    setRentInsuranceInquiryAccepted(false)
    setRopoRentChargingServiceAccepted(false)
    setUpdateStatusToRentedDialogHidden(true)
  }

  const unHideUpdateStatusToRentedDialog = () => {
    setUpdateStatusToRentedClicked(false)
    setDigivakuusDepositAccepted(false)
    setRentInsuranceInquiryAccepted(false)
    setRopoRentChargingServiceAccepted(false)
    setUpdateStatusToRentedDialogHidden(false)
  }

  const proceedToSettingToRented = () => {
    if (selectedApplicationIdForRented === '') {
      return
    }

    setUpdateStatusToRentedClicked(true)
  }

  const setToFollowUpStatus = () => {
    setIsLoading({ loading: true, func: 'setToFollowUp' })
    updateStatus(ApartmentStatus.followUp)
    toast.info(t('paymentPopup.messages.followUpOk'))
  }

  const collateralEligible = (): boolean => {
    if (
      !selectedApplicationIdForRented ||
      selectedApplicationIdForRented === '' ||
      selectedApplicationIdForRented === 'someoneElse' ||
      !user?.collateralVerified
    ) {
      return false
    }

    const allApplications = Object.values(applications || {}).flat()

    const forCurrentApartment = allApplications.filter(
      (a: any) => a.apartmentId === apartment.apartmentId
    )

    if (forCurrentApartment.length === 0) return false

    const targetApp =
      forCurrentApartment.length === 1
        ? forCurrentApartment[0]
        : forCurrentApartment.find(
            (a: any) => a.applicationId === selectedApplicationIdForRented
          ) || forCurrentApartment[0]

    if (!targetApp) return false

    if (targetApp?.metadata?.digivakuusDepositCreated === true) {
      return false
    }

    const hasEligibleApplicant =
      Array.isArray(targetApp.applicants) &&
      targetApp.applicants.some(
        (appl: any) =>
          appl?.acceptedDigivakuusDepositService === true &&
          appl?.digivakuusEligibility === true
      )

    return hasEligibleApplicant
  }

  const showExistingDigivakuusProducts = collateralEligible()

  const updateStatusToRented = async (digivakuusAccepted: boolean) => {
    if (!user) {
      return
    }
    const newStatus = ApartmentStatus.rented
    setIsLoading({ loading: true, func: 'rent' })
    const data: Record<string, unknown> = {
      status: newStatus,
      selectedApplicationIdForRented,
      digivakuusDepositAccepted: digivakuusAccepted
    }
    if (ropoRentChargingServiceAccepted) {
      data.acceptedService = 'ropoRentChargingService'
    } else if (rentInsuranceInquiryAccepted) {
      data.acceptedService = 'insuranceInquiry'
    }
    setUpdateStatusToRentedClicked(false)
    hideUpdateStatusToRentedDialog()
    try {
      await apartmentAPI.UpdateStatusToRented(user.userId, apartmentId, data)
      apartment.status = ApartmentStatus.rented
      localStorage.setItem('statusUpdatedToRented', 'true')
      const message = t('apartment.overview.setRentedSuccess')
      toast.info(message, { autoClose: 25000 })
    } catch (error) {
      const message = t('apartment.overview.setRentedFailed')
      toast.info(message, { autoClose: 25000 })
    }
    setIsLoading({ loading: false, func: '' })
  }

  useEffect(() => {
    const statusUpdatedToRented = localStorage.getItem('statusUpdatedToRented')
    if (statusUpdatedToRented) {
      hideUpdateStatusToRentedDialog()
      localStorage.removeItem('statusUpdatedToRented')
    }
  }, [])

  if (!information || !confirmation) {
    return null
  }

  return (
    <div className={`${classes.informationContainer} ${classes.adminPanelContainer}`}>
      <div className={classes.adminPanelActions}>
        {showFollowUpActions &&
          (status !== ApartmentStatus.followUp ? (
            hasFollowUpRight ? (
              <Dialog
                dialogId='FollowUpStatusConfirm'
                color='secondary'
                size='small'
                maxWidth='sm'
                fullScreenDialogMobile={true}
                buttonLabel={
                  isLoading.loading && isLoading.func === 'setToFollowUp' ? (
                    <CircularProgress size={23} style={{ color: 'white' }} />
                  ) : (
                    t('adminApartments.changeStatus.toFollowUp')
                  )
                }
                buttonClassName={`${classes.startMarketing} ${classes.followUpButton}`}
                confirmDialogSubmitAction={setToFollowUpStatus}
                disabled={isLoading.loading && isLoading.func === 'setToFollowUp'}
                Content={<Message message={t('apartment.listingPauseHelper')} />}
              />
            ) : (
              <Dialog
                dialogId='FollowUpStatusPaymentPopup'
                color='secondary'
                size='large'
                maxWidth='md'
                fullScreenDialogMobile={true}
                buttonLabel={t('adminApartments.changeStatus.toFollowUp')}
                buttonClassName={`${classes.startMarketing} ${classes.followUpButton}`}
                Content={
                  <PaymentPopup
                    productTags={['setToFollowUpStatus']}
                    headertext={t('paymentPopup.headerTexts.setToFollowUpStatus')}
                    infotext={t('paymentPopup.suggestTexts.setToFollowUpStatus')}
                    apartment={apartment}
                  />
                }
              />
            )
          ) : (
            <Button
              className={`${classes.startMarketing} ${classes.followUpButton}`}
              onClick={() => {
                setIsLoading({
                  loading: true,
                  func: 'followUpToMarketing'
                })
                updateStatus(ApartmentStatus.marketing)
              }}
              disabled={
                isLoading.loading && isLoading.func === 'followUpToMarketing'
              }
            >
              {isLoading.loading && isLoading.func === 'followUpToMarketing' ? (
                <CircularProgress size={23} style={{ color: 'white' }} />
              ) : (
                t('adminApartments.changeStatus.rentedToMarketing')
              )}
            </Button>
          ))}
        <Button
          component={RouterLink}
          to={getStepPath(apartment, true)}
          size='small'
          style={{ marginRight: '10px' }}
          className={classes.startMarketing}
        >
          {t('apartmentOverview.editApartment')}
        </Button>
        <div style={{ width: '100%' }}>
          <MediaDialogButton
            t={t}
            classes={classes}
            apartment={apartment}
            files={files}
            buttonClassName={classes.startMarketing}
          />
        </div>
        {status === 'marketing' && isAdmin && (
          <Dialog
            dialogId='UpdateStatusToCancelled'
            disabled={isApartmentCancelledDisabled(status)}
            color='secondary'
            paperMaxWidth='1070px'
            title={t('apartments.cancel.label')}
            buttonLabel={
              isLoading.loading && isLoading.func === 'cancel' ? (
                <CircularProgress
                  size={23}
                  style={{ color: '#ff0000' }}
                />
              ) : (
                t('adminApartments.changeStatus.toCancelled')
              )
            }
            buttonClassName={classes.cancelApartment}
            confirmDialogSubmitAction={() => {
              setIsLoading({
                loading: true,
                func: 'cancel'
              })
              updateStatus(ApartmentStatus.cancelled)
            }}
            Content={
              <Message
                message={t('apartments.cancel.confirmMessage', {
                  address: path(['address'], information) || ''
                })}
              />
            }
          />
        )}
        {status === 'confirmed' && isAdmin && (
          <Dialog
            dialogId='UpdateStatusToCancelled'
            disabled={isApartmentCancelledDisabled(status)}
            color='secondary'
            paperMaxWidth='1070px'
            title={t('apartments.cancel.label')}
            buttonLabel={
              isLoading.loading && isLoading.func === 'cancel' ? (
                <CircularProgress
                  size={23}
                  style={{ color: '#ff0000' }}
                />
              ) : (
                t('adminApartments.changeStatus.toCancelled')
              )
            }
            buttonClassName={classes.cancelApartment}
            confirmDialogSubmitAction={() => {
              setIsLoading({
                loading: true,
                func: 'cancel'
              })
              updateStatus(ApartmentStatus.cancelled)
            }}
            Content={
              <Message
                message={t('apartments.cancel.confirmMessage', {
                  address: path(['address'], information) || ''
                })}
              />
            }
          />
        )}
        {status === 'rentContractWanted' && (
          <Button
            size='small'
            className={classes.cancelApartment}
            disabled={isApartmentMarketingDisabled(status)}
            onClick={() => {
              setIsLoading({
                loading: true,
                func: 'market'
              })
              redirectToPaymentPage()
            }}
          >
            {isLoading.loading && isLoading.func === 'market' ? (
              <CircularProgress
                size={23}
                style={{ color: '#ff0000' }}
              />
            ) : (
              t('adminApartments.changeStatus.rentedToMarketing')
            )}
          </Button>
        )}

        {showFollowUpActions && (
          <>
            {updateStatusToRentedDialogHidden ? (
              <Button
                className={classes.startMarketing}
                style={{ textTransform: 'uppercase' }}
                onClick={unHideUpdateStatusToRentedDialog}
                disabled={isLoading.loading && isLoading.func === 'rent'}
              >
                {isLoading.loading && isLoading.func === 'rent' ? (
                  <CircularProgress size={23} style={{ color: 'white' }} />
                ) : (
                  t('adminApartments.changeStatus.toRented')
                )}
              </Button>
            ) : (
              <Dialog
                maxWidth='md'
                onClose={() => {
                  setUpdateStatusToRentedClicked(false)
                  setselectedApplicationIdForRented('')
                  setDigivakuusDepositAccepted(false)
                  setRentInsuranceInquiryAccepted(false)
                }}
                hideDialog={updateStatusToRentedDialogHidden}
                dialogId='UpdateStatusToRented'
                buttonClassName={classes.startMarketing}
                agreeButtonDisabled={selectedApplicationIdForRented === ''}
                buttonLabel={
                  isLoading.loading && isLoading.func === 'rent' ? (
                    <CircularProgress size={23} style={{ color: 'white' }} />
                  ) : (
                    t('adminApartments.changeStatus.toRented')
                  )
                }
                fullScreenDialogMobile={true}
                Content={
                  <>
                    {!updateStatusToRentedClicked ? (
                      <>
                        <Grid container spacing={2} style={{ marginTop: 16 }}>
                          <Grid item sm={12} style={{ marginTop: '-16px' }}>
                            <Message
                              message={t('apartments.rented.confirmMessageGeneral', {
                                applicantName: selectedContractWantedName || t('application.noApplicationFound')
                              })}
                            />
                          </Grid>

                          <Grid item sm={12} style={{ marginTop: '-16px' }}>
                            <select
                              className='select-dropdown'
                              onChange={handleApplicationChange}
                              value={selectedApplicationIdForRented}
                            >
                              <option value='' disabled hidden>{t('apartments.rented.chooseTenant')}</option>
                              {Object.values(applications)
                                .flat()
                                .filter(application => application.apartmentId === apartmentId && application.status !== 'rejected' && application.status !== 'notInterested')
                                .map((application, index) => (
                                  <option key={index} value={application.applicationId}>
                                    {application.data.name}
                                  </option>
                                ))}
                              <option value='someoneElse'>{t('apartments.rented.someoneElse')}</option>
                            </select>
                          </Grid>

                          <Grid item sm={12} style={{ marginTop: '-16px' }}>
                            <Suggest
                              content={t('apartments.rented.chooseTenantHelper')}
                              containerMaxWidth='100%'
                              contentMaxWidth='100%'
                              isModal
                              padding='25px 0px 15px 0'
                            />
                          </Grid>

                          <Grid item xs={6} style={{ paddingRight: 8 }}>
                            <Button
                              fullWidth
                              style={{
                                boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.15)',
                                padding: '8px'
                              }}
                              onClick={() => hideUpdateStatusToRentedDialog()}
                            >
                              {t('apartments.rented.cancel')}
                            </Button>
                          </Grid>
                          <Grid item xs={6} style={{ paddingLeft: 8 }}>
                            <Button
                              variant='contained'
                              fullWidth
                              className={classes.primaryBtn}
                              onClick={() => proceedToSettingToRented()}
                            >
                              {t('apartments.rented.accept')}
                            </Button>
                          </Grid>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid container justify='center' style={{ margin: 'auto' }}>
                          <Typography variant='h5' align='center' className={classes.modalTitle} style={{ marginTop: '8px' }}>
                            {t('apartments.rented.depositServiceTitle')}
                          </Typography>

                          <Grid item sm={12} style={{ marginTop: '-16px' }}>
                            <Suggest isModal containerMaxWidth='100%' contentMaxWidth='100%' content={t('apartments.rented.depositServiceInfo')} />
                          </Grid>

                          {showExistingDigivakuusProducts && (
                            <div className={classes.acceptBox}>
                              <div className={classes.acceptRow}>
                                <Checkbox
                                  classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                                  checked={digivakuusDepositAccepted}
                                  onChange={e => setDigivakuusDepositAccepted(e.target.checked)}
                                />
                                <div className={classes.acceptTexts}>
                                  <Typography className={classes.acceptHeader}>
                                    {t('apartments.rented.depositServiceCheckboxHeader')}
                                  </Typography>
                                  <Typography className={classes.acceptText}>
                                    {t('apartments.rented.depositServiceCheckboxContent')}
                                  </Typography>
                                  <div className={classes.digivakuusFaqInlineWrapper}>
                                    <DigivakuusFaqLink
                                      t={t}
                                      variant='landlord'
                                      buttonClassName={classes.digivakuusFaqButton}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {showExistingDigivakuusProducts && (
                            <div className={classes.acceptBox}>
                              <div className={classes.acceptRow}>
                                <Checkbox
                                  classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                                  checked={rentInsuranceInquiryAccepted}
                                  onChange={e => setRentInsuranceInquiryAccepted(e.target.checked)}
                                />
                                <div className={classes.acceptTexts}>
                                  <Typography className={classes.acceptHeader}>
                                    {t('apartments.rented.rentChargingInquiryTitle')}
                                  </Typography>
                                  <Typography className={classes.acceptText}>
                                    {t('apartments.rented.rentChargingInquiryInfo')}
                                  </Typography>
                                  <div className={classes.digivakuusFaqInlineWrapper}>
                                    <DigivakuusFaqLink
                                      t={t}
                                      variant='rentInsurance'
                                      buttonClassName={classes.digivakuusFaqButton}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className={classes.acceptBox}>
                            <div className={classes.acceptRow}>
                              <Checkbox
                                classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                                checked={ropoRentChargingServiceAccepted}
                                onChange={e => setRopoRentChargingServiceAccepted(e.target.checked)}
                              />
                              <div className={classes.acceptTexts}>
                                <Typography className={classes.acceptHeader}>
                                  {t('apartments.rented.ropoRentChargingTitle')}
                                </Typography>
                                <Typography className={classes.acceptText}>
                                  <Trans
                                    t={t}
                                    i18nKey='apartments.rented.ropoRentChargingInfo'
                                    components={{ strong: <strong /> }}
                                  />
                                </Typography>
                                <div className={classes.cashbackBox}>
                                  <Typography className={classes.cashbackTitle}>
                                    {t('apartments.rented.ropoRentChargingCashbackTitle')}
                                  </Typography>
                                  <Typography className={classes.cashbackText}>
                                    <Trans
                                      t={t}
                                      i18nKey='apartments.rented.ropoRentChargingCashbackInfo'
                                      components={{ strong: <strong /> }}
                                    />
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          </div>

                          <Grid container spacing={2} style={{ marginTop: '6px' }}>
                            <Grid item xs={12}>
                              <Button
                                variant='contained'
                                fullWidth
                                className={classes.primaryBtn}
                                onClick={() => updateStatusToRented(digivakuusDepositAccepted)}
                              >
                                {t('apartments.rented.submit')}
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                }
              />
            )}
          </>
        )}
        {(status === 'cancelled' || status === 'rented' || status === 'confirmed') && (
          <Button
            size='small'
            className={classes.startMarketing}
            disabled={isApartmentMarketingDisabled(status)}
            onClick={() => {
              setIsLoading({
                loading: true,
                func: 'market'
              })
              redirectToPaymentPage()
            }}
          >
            {isLoading.loading && isLoading.func === 'market' ? (
              <CircularProgress size={23} style={{ color: 'white' }} />
            ) : (
              t('adminApartments.changeStatus.toMarketing')
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

const ApartmentInfo: FC<ApartmentInfoProps> = ({
  t,
  classes,
  apartment,
  apartment: {
    information,
    confirmation,
    code
  },
  user,
  isAdmin
}) => {
  const descriptionText =
    information?.notes && information.notes.trim().length > 0
      ? information.notes
      : ''
  const descriptionIntro =
    confirmation?.vuokraoviCollateral
      ? t('apartment.description.noDepositIntro')
      : ''
  const landlordNameRaw = user?.data?.name
  const landlordName = typeof landlordNameRaw === 'string' ? landlordNameRaw.trim() : ''
  const landlordPhoneRaw = user?.data?.phoneNumber
  const landlordPhone =
    typeof landlordPhoneRaw === 'string' ? landlordPhoneRaw.trim() : ''
  const formattedPhone =
    landlordPhone && landlordPhone.length > 0
      ? landlordPhone.startsWith('+')
        ? landlordPhone
        : `+${landlordPhone}`
      : ''
  let contactInfoParagraph = ''
  if (
    confirmation?.contactInfoPublicOnPlatforms &&
    (landlordName.length > 0 || landlordPhone.length > 0)
  ) {
    const contactInfoLines = [t('apartment.description.contactInfoTitle')]
    const detailLines: string[] = []
    if (landlordName.length > 0) {
      detailLines.push(
        t('apartment.description.contactInfoLandlord', { name: landlordName })
      )
    }
    if (formattedPhone.length > 0) {
      detailLines.push(
        t('apartment.description.contactInfoPhone', { phoneNumber: formattedPhone })
      )
    }
    if (detailLines.length > 0) {
      contactInfoLines.push('', ...detailLines)
      contactInfoParagraph = contactInfoLines.join('\n')
    }
  }
  const descriptionContent = [descriptionIntro, descriptionText, contactInfoParagraph]
    .filter((paragraph) => paragraph && paragraph.trim().length > 0)
    .join('\n\n')
  const apartmentInformationRecord = (information || {}) as Record<
    string,
    unknown
  >
  const roomsLabel =
    typeof information?.rooms === 'string' ? information.rooms : ''
  const kitchenLabel =
    typeof information?.kitchen === 'string'
      ? information.kitchen.toLowerCase()
      : ''
  const saunaLabel =
    information?.sauna && information.sauna !== 'no' ? '+ s' : ''
  let balconyLabel = ''
  if (information?.balcony && information.balcony !== 'no') {
    balconyLabel =
      information.balcony === 'glass'
        ? '+ lasit. p'
        : information.balcony === 'balconet'
        ? '+ ransk. p'
        : '+ p'
  }
  const closetLabel = information?.hasBuiltInClosetCheckBoxProperty ? '+ vh' : ''
  const roomKitchenSegment = [
    roomsLabel,
    kitchenLabel && `+ ${kitchenLabel}`,
    saunaLabel,
    balconyLabel,
    closetLabel
  ]
    .filter((value) => value && value.trim().length > 0)
    .join(' ')
  const basicInfoData = {
    ...getApartmentBuildingInfo(confirmation, information),
    ...getAdditionalPropertiesForOverview(information)
  }
  if (roomKitchenSegment.trim()) {
    basicInfoData.rooms = roomKitchenSegment
  }
  if (information?.damages) {
    basicInfoData.damages = information.damages
  }
  if (information?.renovations) {
    basicInfoData.renovations = information.renovations
  }
  const checkboxInformationOrder = getSortedKeyArray(
    returnTrueFirst,
    Object.keys(CheckboxPropertyKeys || {}),
    apartmentInformationRecord
  )
  const checkboxListData = checkboxInformationOrder.reduce<Record<string, string>>(
    (acc, checkboxKey) => {
      const propertyValue = Boolean(apartmentInformationRecord[checkboxKey])
      acc[checkboxKey] = propertyValue ? 'general.true' : 'general.false'
      return acc
    },
    {}
  )
  const additionalInfoExtras: { key: string; value?: string }[] = []
  const extraKeysForOrder: string[] = []
  additionalInfoExtras.forEach(({ key, value }) => {
    if (value) {
      checkboxListData[key] = value
      extraKeysForOrder.push(key)
    }
  })
  const additionalInformationOrder = [...extraKeysForOrder, ...checkboxInformationOrder]
  type SectionKey = 'basicInfo' | 'additionalInfo' | 'confirmation'
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    basicInfo: true,
    additionalInfo: true,
    confirmation: true
  })
  const toggleSection =
    (section: SectionKey) =>
    () => {
      setOpenSections((prev) => ({
        ...prev,
        [section]: !prev[section]
      }))
    }
  const handleSectionKeyToggle =
    (section: SectionKey) =>
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        toggleSection(section)()
      }
    }

  return (
    <Fragment>
      {information && confirmation && (
        <>
          {isAdmin && apartment && (
            <Fragment>
              <Typography
                variant='h6'
                style={{ color: '#3f51b5', marginBottom: '10px' }}
              >
                {`${t('apartment.overview.apartmentCode')}: ${code}`}
              </Typography>
            </Fragment>
          )}

          {descriptionContent && (
            <div className={`${classes.informationContainer} ${classes.mt20} ${classes.descriptionContainer}`}>
              <Typography variant='body1'>
                <pre className='MuiTypography-root MuiTypography-body1'>
                  {descriptionContent}
                </pre>
              </Typography>
            </div>
          )}

          <div
            className={`${classes.sectionTitleRow} ${classes.sectionTitleRowFirst}`}
            role='button'
            tabIndex={0}
            onClick={toggleSection('basicInfo')}
            onKeyPress={handleSectionKeyToggle('basicInfo')}
          >
            <span className={classes.sectionIcon}>
              {openSections.basicInfo ? '-' : '+'}
            </span>
            <Typography variant='subtitle1'>
              {t('apartmentOverview.basicInfoHeading')}
            </Typography>
          </div>
          <div className={classes.sectionDivider} />
          {openSections.basicInfo && (
            <div className={classes.infoSectionContent}>
              <ApartmentInformationList
                apartmentInformation={basicInfoData}
                t={t}
              />
            </div>
          )}
          <div
            className={classes.sectionTitleRow}
            role='button'
            tabIndex={0}
            onClick={toggleSection('additionalInfo')}
            onKeyPress={handleSectionKeyToggle('additionalInfo')}
          >
            <span className={classes.sectionIcon}>
              {openSections.additionalInfo ? '-' : '+'}
            </span>
            <Typography variant='subtitle1'>
              {t('apartmentOverview.additionalInfoHeading')}
            </Typography>
          </div>
          <div className={classes.sectionDivider} />
          {openSections.additionalInfo && (
            <div className={classes.infoSectionContent}>
              <CustomList
                t={t}
                translationPath='apartment.list.information.'
                data={checkboxListData}
                informationOrder={additionalInformationOrder}
              />
            </div>
          )}
          {confirmation && information && (
            <>
              <div
                className={classes.sectionTitleRow}
                role='button'
                tabIndex={0}
                onClick={toggleSection('confirmation')}
                onKeyPress={handleSectionKeyToggle('confirmation')}
              >
                <span className={classes.sectionIcon}>
                  {openSections.confirmation ? '-' : '+'}
                </span>
                <Typography variant='subtitle1'>
                  {t('apartment.confirmation.header')}
                </Typography>
              </div>
              <div className={classes.sectionDivider} />
              {openSections.confirmation && (
                <div className={classes.infoSectionContent}>
                  <ApartmentConfirmationList
                    apartmentConfirmation={confirmation}
                    apartmentInformation={information}
                    t={t}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </Fragment>
  )
}

export default ApartmentInfo
